import { FileVersionsList } from './FileVersionsList';
import { getInputFilesFromRemote } from '@/app/features/products/common/services';
import { hasPermissionToRender, modalUtils } from '@/app/core/utils';
import { FieldMappings } from '@/app/features/products/common/components/frontend-processing/models';
import { getCurrentFeatureCode } from '@/app/core/auth';
import { useCallback, useRef, useState, useEffect } from 'react';
import { Table } from 'antd';
import { extend as dayJSExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import {
  HeaderInfo,
  InputRemoteFile,
  fetchAndEnforceApiGatewayResponseData,
  InputLabelsMap,
  DashboardPermissions,
} from '@ssar-app/domain';
import { Modal } from '@ssar-app/uikit';
import {
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
} from '@chakra-ui/react';
import {
  AiOutlineReload,
  AiOutlineSearch,
  AiOutlineUpload,
  AiOutlineDown,
  AiOutlineRight,
} from 'react-icons/ai/index.js';

dayJSExtend(utcPlugin);

interface InputRemoteFileSelectorProps {
  subCode: string;
  isCompact?: boolean;
  beforeUpload: (
    headersInfo: HeaderInfo,
    file?: File,
    content?: string[],
    experianFileName?: string,
    clientFileName?: string,
    totalRecords?: number,
    intentType?: string,
    fileId?: string,
  ) => void;
  productName: string;
  onSuccess: (
    clientFileName: string,
    serverFileName: string,
    fieldMappings: FieldMappings[],
    hasHeaders?: boolean,
    recordsExpected?: number,
  ) => void;
  inputLabelsMap?: InputLabelsMap;
  isModalVisible: boolean;
  setIsModalVisible: (val1: boolean) => void;
}

export const InputRemoteFileSelector = (
  props: InputRemoteFileSelectorProps,
) => {
  const { isModalVisible, setIsModalVisible } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const remoteData = useRef<InputRemoteFile[]>([]);
  const [tempRemoteData, setRemoteTempData] = useState<InputRemoteFile[]>([]);
  const [fileSearchText, setFileSearchText] = useState<string>('');
  const isAdmin = hasPermissionToRender(
    getCurrentFeatureCode(),
    DashboardPermissions.MayViewAdminColumns,
  );

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      sorter: (a: InputRemoteFile, b: InputRemoteFile) =>
        a.fileName.localeCompare(b.fileName),
    },
    {
      title: 'File Type',
      dataIndex: 'fileType',
      sorter: true,
    },
    {
      title: 'Intent Type',
      dataIndex: 'intentType',
      sorter: true,
    },
    isAdmin
      ? {
          title: 'Product Name',
          dataIndex: 'productName',
          sorter: true,
        }
      : {},
  ];

  const getData = (): void => {
    setIsLoading(true);
    setFileSearchText('');
    fetchAndEnforceApiGatewayResponseData(() =>
      getInputFilesFromRemote(props.subCode, props.productName),
    )
      .then((data) => {
        data = data?.map((item) => {
          const file: InputRemoteFile = item;
          // file.key = file.fileId;
          return file;
        });
        remoteData.current = data || [];
        setRemoteTempData(data || []);
      })
      .catch((error) => {
        modalUtils.showError(error, 'Error while getting input files.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getDataWrapper = useCallback(() => getData(), []);

  useEffect(() => {
    if (isModalVisible) {
      getDataWrapper();
    }
  }, [isModalVisible, getDataWrapper]);

  useEffect(() => {
    setPageNo(1);
    setRemoteTempData(
      remoteData.current.filter((file) =>
        file.fileName.toLowerCase().includes(fileSearchText.toLowerCase()),
      ),
    );
  }, [fileSearchText]);

  return (
    <>
      {props.isCompact && (
        <Button
          colorScheme='gray'
          variant='outline'
          leftIcon={<AiOutlineUpload />}
          onClick={() => setIsModalVisible(true)}
          ml={2.5}
          height='8'
        >
          Select file already transferred to Experian
        </Button>
      )}
      {!props.isCompact && (
        <Button
          colorScheme='gray'
          variant='outline'
          leftIcon={<AiOutlineUpload />}
          onClick={() => setIsModalVisible(true)}
          w={96}
          height='10'
          border='1px solid'
          borderColor={'gray.300'}
        >
          Select file already transferred to Experian
        </Button>
      )}
      {isModalVisible && (
        <Modal
          header={
            <Grid alignItems='center' templateColumns='repeat(12, 1fr)' mr={9}>
              <GridItem colSpan={6}>
                Select a file already transferred to Experian
              </GridItem>
              <GridItem
                colSpan={6}
                ml='auto'
                display='flex'
                alignItems='center'
                flexDirection='row'
              >
                <InputGroup size='sm'>
                  <Input
                    value={fileSearchText}
                    disabled={isLoading}
                    placeholder='Search Files by Name'
                    isInvalid={false}
                    onChange={(e) =>
                      setFileSearchText(e.target.value.trim().toLowerCase())
                    }
                    spellCheck={false}
                  />
                  <InputRightAddon>
                    <Icon as={AiOutlineSearch} />
                  </InputRightAddon>
                </InputGroup>
                <Icon
                  as={AiOutlineReload}
                  onClick={getData}
                  float='right'
                  ml={4}
                />
              </GridItem>
            </Grid>
          }
          headerProps={{ py: 2 }}
          isOpen
          contentProps={{ maxW: '95vw' }}
          bodyProps={{ px: 4 }}
          onClose={() => setIsModalVisible(false)}
          showOk={false}
          scrollBehavior='outside'
        >
          <Table
            size='small'
            loading={isLoading}
            columns={columns}
            dataSource={tempRemoteData}
            rowKey={(row, index) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              `${row.fileName || (row as any)['_id']}_${index}`
            }
            // scroll={{ scrollToFirstRowOnChange: true, y: 300 }}
            pagination={{
              current: pageNo,
              onChange: (page) => setPageNo(page),
              total: remoteData.current.length,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomLeft'],
              showSizeChanger: true,
              simple: false,
            }}
            expandable={{
              expandedRowClassName: () => 'versions-expanded-row',
              columnWidth: '60px',
              expandIcon: ({ expanded, onExpand, record }) => {
                const versionCount = record?.versions.length || 0;
                return (
                  <HStack
                    border={'1px'}
                    borderRadius={12}
                    pl={1}
                    pr={1}
                    onClick={(e) => onExpand(record, e)}
                    cursor='pointer'
                  >
                    <Text w={4}>
                      {versionCount < 10 && versionCount > 0
                        ? '0' + versionCount
                        : versionCount}
                    </Text>
                    {expanded ? (
                      <Icon as={AiOutlineDown} />
                    ) : (
                      <Icon as={AiOutlineRight} />
                    )}
                  </HStack>
                );
              },
              expandedRowRender: (record) => (
                <>
                  {(record?.versions.length || 0) > 0 && (
                    <FileVersionsList
                      record={record}
                      beforeUpload={props.beforeUpload}
                      onSuccess={props.onSuccess}
                      inputLabelsMap={props.inputLabelsMap}
                      setIsModalVisible={setIsModalVisible}
                      productName={props.productName}
                    />
                  )}
                </>
              ),
            }}
          ></Table>
        </Modal>
      )}
    </>
  );
};
